import * as React from 'react';
import { AlertMessage, AlertType, ButtonType, Checkbox, CustomButton, CustomDatePicker, CustomSubmitButton, Form, HiddenInput, Modal, ModalBody, ModalFooter, ModalSize, NumberInput, Select, SpinnerWrapper, Textarea } from "soprasteria-ui-library";
import { IListItem } from 'soprasteria-ui-library/dist/models/IFormInput';
import { useAuth } from '../../../../App';
import { CarePerformerApi } from '../../../../api/CarePerformerApi';
import { ReferralTreatmentReportingApi } from '../../../../api/ReferralTreatmentReportingApi';
import { formatDate } from '../../../../helpers/DateHelper';
import { setMessage } from '../../../../helpers/MessageHelper';
import { IReferralSavePatientTreatments, IReferralTreatmentCode } from '../../../../models/IReferral';
import { TreatmentsDropdown } from '../../TreatmentReport/TreatmentsDropdown';
import { NoCarePerformersFoundMessage } from '../../../../utils/CarePerformerConstants';

interface IReferralTreatmentModal {
    referralId?: number;
    specialityId: number;
    renderModal: boolean;
    onSubmitSuccess: (reopenTreatmentsModal: boolean) => void;
    onClose: () => void;
    registerMultipleTreatments: boolean;
    setRegisterMultipleTreatments: (registerMultipleTreatments: boolean) => void;
    treatmentCodes?: IReferralTreatmentCode[];
    referralClosedDate?: Date;
    recievedDate?: Date;
}

interface IAddTreatment {
    treatmentCodeId: string;
    treatmentCode?: string;
    commentReqired?: boolean;
    priceRequired?: boolean;
}


export const ReferralTreatmentModal: React.FunctionComponent<IReferralTreatmentModal> = ({ referralId, specialityId, renderModal, onSubmitSuccess, onClose, registerMultipleTreatments, setRegisterMultipleTreatments, treatmentCodes, referralClosedDate, recievedDate }) => {

    const auth = useAuth();
    const api = new ReferralTreatmentReportingApi(auth);
    const carePerformerApi = new CarePerformerApi(auth);
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>();
    const [noCarePerformersMessage, setNoCarePerformersMessage] = React.useState<string>();
    const [patientTreatments, setPatientTreatments] = React.useState<IAddTreatment[]>();
    const [carePerformers, setCarePerformers] = React.useState<IListItem[]>([]);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [date, setDate] = React.useState<Date>(new Date());

    React.useEffect(() => {
        if (renderModal) {
            resetFormState();
            document.getElementById("open-add-referral-treatment-modal")?.click();
        }
    }, [renderModal])

    React.useEffect(() => {
        loadCarePerformerDentists();
    }, [date])

    const loadCarePerformerDentists = () => {
        setIsLoading(true);
        carePerformerApi.getCarePerformerDentistsWithDateAndSpeciality(formatDate(date), specialityId, (carePerformersData) => {
            let mappedCarePerformers = carePerformersData.map<IListItem>((carePerformer) => { return { text: carePerformer.carePerformerName, value: carePerformer.carePerformerId.toString() } });
            setCarePerformers(mappedCarePerformers);
            if (mappedCarePerformers.length === 0) {
                const newMessage = setMessage(NoCarePerformersFoundMessage, noCarePerformersMessage ?? "");
                setNoCarePerformersMessage(newMessage);
            } else {
                setNoCarePerformersMessage("");
            }
        }, undefined, () => setIsLoading(false));
    }

    const saveTreatment = (data: IReferralSavePatientTreatments) => {
        if (referralId) {
            setIsSubmitting(true);
            data.referralId = referralId;
            api.saveReferralTreatment(data, () => {
                onClose();
                onSubmitSuccess(registerMultipleTreatments);
            }, (message) => {
                const newMessage = setMessage(message, errorMessage ?? "");
                setErrorMessage(newMessage);
            }, () => {
                setIsSubmitting(false);
            });
        }
    }

    const onTreatmentCodeChange = (value: string | string[] | undefined) => {
        if (Array.isArray(value)) {
            var t = value?.map(treatmentCodeId => ({
                treatmentCodeId: treatmentCodeId,
                treatmentCode: treatmentCodes?.find((treatment) => { return treatment.treatmentCodeId.toString() === treatmentCodeId })?.code,
                commentReqired: treatmentCodes?.find((treatment) => { return treatment.treatmentCodeId.toString() === treatmentCodeId })?.requireComment,
                priceRequired: treatmentCodes?.find((treatment) => { return treatment.treatmentCodeId.toString() === treatmentCodeId })?.requirePrice
            }));
            setPatientTreatments(t);
        }
    }

    const resetFormState = () => {
        setIsSubmitting(false);
        setErrorMessage(undefined);
        setPatientTreatments(undefined);
    }

    const onCancel = () => {
        setRegisterMultipleTreatments(false);
        onClose();
    }

    const setMinimumTreatmentDate = () => {
        if (recievedDate) {
            var currentDate = new Date();
            var minDate = new Date(currentDate);
            minDate.setDate(minDate.getDate() - 90);

            const recievedDateAsDate = new Date(recievedDate);

            return minDate < recievedDateAsDate
                ? recievedDateAsDate
                : minDate;
        }
    }

    const renderPatientTreatmentRow = (treatmentCode: IAddTreatment, key: number) => {
        return <li key={key}>
            <div className="row">
                <div className="col-12">
                    <HiddenInput name={`patientTreatments[${key}].treatmentCodeId`} value={treatmentCode.treatmentCodeId} />
                    {
                        treatmentCode.commentReqired ?
                            <Textarea key={treatmentCode.treatmentCodeId} label={`Motivering för ${treatmentCode.treatmentCode}`} name={`patientTreatments[${key}].comment`} inlineLabel required />
                            : null
                    }
                    {
                        treatmentCode.priceRequired ?
                            <NumberInput key={treatmentCode.treatmentCodeId} label={`Pris för ${treatmentCode.treatmentCode}`} name={`patientTreatments[${key}].price]`} minValue={1} inlineLabel required />
                            : null
                    }
                </div>
            </div>
        </li>
    }

    const onTreatmentDateChange = (treatmentDate: Date | null) => {
        if (treatmentDate === null) {
            setDate(new Date());
        } else {
            setDate(treatmentDate);
        }
    }

    return (
        <>
            {
                renderModal ?
                    <Modal modalSize={ModalSize.large} header="Lägg till åtgärdskod" id="add-referral-treatment-modal">
                        <SpinnerWrapper isLoading={treatmentCodes === undefined}>
                            <Form onSubmit={saveTreatment} >
                                <ModalBody>
                                    <AlertMessage alertType={AlertType.danger} duration={3000}>{errorMessage}</AlertMessage>
                                    <AlertMessage alertType={AlertType.danger} duration={10000}>{noCarePerformersMessage}</AlertMessage>
                                    <div className="row">
                                        <div className="col-12">
                                            <CustomDatePicker name="treatmentDate"
                                                label="Behandlingsdatum"  
                                                tooltipDescription="Avser datumet när behandlingsåtgärden utfördes på patienten, ska överensstämma med journalen" 
                                                onChange={onTreatmentDateChange} 
                                                min={setMinimumTreatmentDate()} 
                                                max={referralClosedDate ? new Date(referralClosedDate) : new Date()} 
                                                required 
                                                inlineLabel />
                                            <Select name="carePerformerId"
                                                label="Behandlare"
                                                placeholder="Välj en behandlare"
                                                options={carePerformers}
                                                isLoading={isLoading}
                                                inlineLabel
                                                required />
                                            {
                                                treatmentCodes ?
                                                    <div>
                                                        <TreatmentsDropdown label="Åtgärdskoder" treatments={treatmentCodes} name="" isMultiple onValueChange={onTreatmentCodeChange} placeholder="Välj åtgärdskoder" />
                                                        
                                                    </div>
                                                    : null
                                            }
                                            {
                                                <ul className="list-no-styling">
                                                    {
                                                        patientTreatments?.map((treatmentCodeId, index) =>
                                                            renderPatientTreatmentRow(treatmentCodeId, index)
                                                        )
                                                    }
                                                </ul>
                                            }
                                        
                                        </div>
                                    </div>
                                    <p style={{ color: 'grey', bottom: '0' }}>Det går att rapportera flera åtgärdskoder samtidigt för samma behandlingsdatum.</p>
                                </ModalBody>
                                <ModalFooter>
                                    <div className="col-6 text-left">
                                        <Checkbox name="multipleTreatments"
                                            id="multipleTreatments"
                                            label="Lägg till flera åtgärdskoder"
                                            value="false"
                                            checked={registerMultipleTreatments}
                                            onChange={() => setRegisterMultipleTreatments(!registerMultipleTreatments)}
                                            labelCol={9}
                                            inputCol={3} />
                                    </div>
                                    <div className="col-6 text-right">
                                        <CustomButton buttonType={ButtonType.general} id="close-add-referral-treatment-modal" buttonText="Avbryt" data-dismiss="modal" onClick={() => onCancel()} />
                                        <CustomSubmitButton isLoading={isSubmitting}>Spara</CustomSubmitButton>
                                        <input type="hidden" data-toggle="modal" id="open-add-referral-treatment-modal" data-target="#add-referral-treatment-modal" />
                                    </div>
                                </ModalFooter>
                            </Form>
                        </SpinnerWrapper>
                    </Modal>
                    : null
            }
        </>
    )
}